// PTZ-kamera-kontrakt — NJORD-kystpostens eneste sensor som også er en EFFEKTOR.
//
// HVA DETTE ER: datamodellen og geometrien for et pan/tilt/zoom-kamera på en
// kystobservasjonspost. Kameraet er to ting samtidig:
//   • SENSOR — gir visuell bekreftelse/identifikasjon (den ENESTE kilden som kan
//     skille en fisker fra en USV; radar og RF sier bare «noe er der»).
//   • EFFEKTOR — det kan PEKES. Derfor har fila kommandoer og en løsning
//     («PekLosning»), ikke bare målinger.
//
// HOVEDFUNKSJONEN er slew-to-cue: radar/Kraken-RF/hydrofon gir en peiling (og
// noen ganger avstand), kameraet svinger dit for identifikasjon. Alt annet her
// finnes for å gjøre den funksjonen ærlig.
//
// ── ONVIF-FUNN (verifisert mot ONVIF PTZ Service Specification v26.06) ──────
// 1. «Profile E» FINNES IKKE. ONVIF har A, C, D, G, M, S, T (+ V som RC).
//    PTZ ligger i Profile S §8.3 og er CONDITIONAL («if supported»), og
//    AbsoluteMove er en EGEN betinget underfunksjon (Profile S §8.4). Et
//    Profile-S-kamera kan altså være helt uten absolutt peking. Derfor er
//    `PtzKapabilitet` en del av kontrakten, og `pekMotPunkt` merker
//    «ingen-absolutt-peking» framfor å late som kommandoen vil virke.
// 2. PTZ-rommet er NORMALISERT, ikke grader (spec §5.7.1.1):
//    PanTiltSpaces/PositionGenericSpace har Xrange og Yrange −1..1, definert som
//    «the full range of the PTZ unit normalized to the range -1 to 1».
//    Zoom (§5.7.1.3): ZoomSpaces/PositionGenericSpace 0 (wide) .. 1 (tele).
//    Dette er fella: −1..1 betyr INGENTING uten kameraets mekaniske grenser i
//    grader. Oversettelsen er derfor en del av leveransen
//    (`normalisertTilGrader` / `graderTilNormalisert`).
// 3. Det FINNES et gradrom (§5.7.1.2): PanTiltSpaces/SphericalPositionSpaceDegrees.
//    Der er tilt 0° = kameralinsen parallell med taket (dvs. mot horisonten),
//    negativ tilt = nedover (dome-kameraer typisk 0..−90), og økende pan er
//    med klokka. XRange er −INF..INF (pan er uavgrenset), YRange −180..180.
//    NÅR enheten tilbyr dette rommet SKAL vi bruke det — da slipper vi å gjette
//    på normalisert↔grader (`PtzKapabilitet.gradromStottet`).
// 4. Zoom i mm finnes også (§5.7.1.4): ZoomSpaces/PositionSpaceMillimeter =
//    absolutt brennvidde. Spec sier eksplisitt om det GENERISKE zoomrommet:
//    «There is no assumption about how the generic zoom range is mapped to
//    magnification, FOV or other physical zoom dimension.» Altså: zoom 0.5 er
//    IKKE halv brennvidde. Vår lineære antagelse er merket UVERIFISERT og kan
//    overstyres med en målt kalibreringstabell (`KameraOptikk.zoomKalibrering`).
// 5. Synsfelt fra brennvidde (§5.7.1.4): «the current angle of view can be
//    calculated with the following formula», der d = sensorens størrelse i
//    retningen av interesse og f = brennvidden. Selve formelen er et BILDE i
//    PDF-en og kunne ikke maskinlesbart hentes ut; vi bruker standard
//    tynnlinse-AOV: 2·atan(d / 2f). Formelen er standard optikk, men at den er
//    eksakt slik i spec-bildet er UVERIFISERT.
// 6. ONVIF GetStatus (§5.3) gir Position (kun hvis StatusPosition støttes),
//    MoveStatus (Idle/Moving/Unknown) og valgfritt FieldOfView. Det er speilet i
//    `PtzTilstand` — inkludert at posisjon KAN mangle.
//
// ── HVORFOR VI IKKE GJENBRUKER klv.ts ──────────────────────────────────────
// `klv.ts` er en binær MISB-0601-parser for metadata som følger en STANAG-4609-
// videostrøm (KLV-triplett, BER-lengder). Den beskriver hva et fly SENDER om
// bildet sitt — ikke hvordan et fast montert kystkamera PEKES. Ingen overlapp:
// den brukes videre der video-metadata dekodes, og bør brukes hvis/når dette
// kameraet en dag sender MISB-metadata i strømmen.
//
// ── SIKKERHET (grunnloven §4) ──────────────────────────────────────────────
// Ingen RTSP-URL og ingen legitimasjon i kontrakten. Det er DRIFT. Vi modellerer
// en opak `enhetRef` + ONVIF `mediaProfilToken`; selve adressen hentes i drift med
// Media-tjenestens GetStreamUri (Profile S §8.1). `erTrygtVideoRef` HÅNDHEVER det.
import { z } from 'zod';
import { haversineM, project } from './geo.js';
import { bearingDeg } from './targeting.js';
// ── Vinkel- og jordgeometri-hjelpere ────────────────────────────────────────
const RAD = Math.PI / 180;
const tilDeg = (r) => r / RAD;
const tanDeg = (d) => Math.tan(d * RAD);
const atanDeg = (v) => tilDeg(Math.atan(v));
const klem = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
/** Peiling normalisert til [0,360). */
export function peiling360(deg) {
    if (!Number.isFinite(deg))
        return 0;
    return ((deg % 360) + 360) % 360;
}
/** Vinkel til (−180,180] — brukes overalt der 0/360-overgangen kan bite. */
export function vinkelWrap180(deg) {
    if (!Number.isFinite(deg))
        return 0;
    const v = ((deg % 360) + 360) % 360;
    return v > 180 ? v - 360 : v;
}
export const JORD_RADIUS_M = 6_371_000;
/**
 * Terrestrisk refraksjonskoeffisient (standardatmosfære). Effektiv jordradius er
 * R/(1−k), altså ~15 % «flatere» jord — lysstrålen bøyes nedover og du ser litt
 * lenger enn geometrien tilsier. Konstanten er den vanlige geodetiske verdien;
 * at NETTOPP 0.13 gjelder for en fjordatmosfære med temperaturinversjon er
 * UVERIFISERT (inversjon over kaldt vann kan gi mye større effekt).
 */
export const REFRAKSJON_K = 0.13;
/**
 * Effektiv jordradius (m): R/(1−k) med refraksjon, ellers geometrisk R.
 *
 * EKSPORTERT (ikke bare lokal) fordi `maalpeking.ts` (bølge MAAL-01, horisont+
 * havflate-metoden) trenger PRESIS samme radius for å utlede horisontens
 * dip-vinkel fra `horisontAvstandM` (dip = d_horisont/R′, se kommentaren der).
 * Å regne en egen R′ i `maalpeking.ts` med samme formel ville vært en usynlig
 * duplikat-sannhet: endres `REFRAKSJON_K` her en dag, ville den andre fila
 * stille blitt feil (grunnloven §3).
 */
export const effektivJordradiusM = (medRefraksjon) => medRefraksjon ? JORD_RADIUS_M / (1 - REFRAKSJON_K) : JORD_RADIUS_M;
/** Hvor mye jordkrumningen «senker» et mål på avstand d (meter). d²/2R. */
export function jordkrumningsfallM(avstandM, medRefraksjon = true) {
    const d = Math.max(0, avstandM);
    return (d * d) / (2 * effektivJordradiusM(medRefraksjon));
}
/**
 * Avstand til horisonten for en observatør i `hoydeM`, pluss målets egen
 * horisont (et mål med høyde ses lenger). En USV er nesten flat — derfor er
 * `malHoydeM` default 0, og et sjømål bak denne avstanden er FYSISK usynlig
 * uansett hvor godt kameraet peker.
 */
export function horisontAvstandM(hoydeM, malHoydeM = 0, medRefraksjon = true) {
    const r = effektivJordradiusM(medRefraksjon);
    const d = (h) => (h <= 0 ? 0 : Math.sqrt(2 * r * h));
    return d(hoydeM) + d(malHoydeM);
}
// ── ONVIF-rom-URIer (verifisert, se filhodet) ───────────────────────────────
/** Space-URIene fra spec. Sendes med i AbsoluteMove så enheten ikke gjetter rom. */
export const ONVIF_ROM = {
    panTiltGenerisk: 'http://www.onvif.org/ver10/tptz/PanTiltSpaces/PositionGenericSpace',
    panTiltGraderSfaerisk: 'http://www.onvif.org/ver10/tptz/PanTiltSpaces/SphericalPositionSpaceDegrees',
    zoomGenerisk: 'http://www.onvif.org/ver10/tptz/ZoomSpaces/PositionGenericSpace',
    zoomMillimeter: 'http://www.onvif.org/ver10/tptz/ZoomSpaces/PositionSpaceMillimeter',
};
// ── Video-referanse: id, ALDRI adresse eller legitimasjon ────────────────────
/**
 * Nekter alt som lukter adresse eller hemmelighet. Dette er ikke pynt: en
 * `rtsp://bruker:passord@…` i en kontrakt havner i logger, eventer, kart-tooltips
 * og backuper — og da er kameraet kompromittert for alltid. Strømmen slås opp i
 * drift (ONVIF GetStreamUri) fra `enhetRef`, som bare betyr noe i enhetsregisteret.
 */
export function erTrygtVideoRef(s) {
    if (!s || s.length > 128)
        return false;
    if (/\s/.test(s))
        return false;
    if (s.includes('://') || s.includes('@'))
        return false;
    if (/rtsp|rtmp|https?|onvif:\/\/|passw|passord|secret|hemmelig|token=|user[:=]/i.test(s)) {
        return false;
    }
    return true;
}
const trygtRef = z
    .string()
    .min(1)
    .max(128)
    .refine(erTrygtVideoRef, 'referansen ser ut som en adresse/legitimasjon — kun opak id tillatt');
export const VideoReferanse = z.object({
    /** Opak id for ONVIF-enheten i drifts-/enhetsregisteret. IKKE et vertsnavn. */
    enhetRef: trygtRef,
    /** ONVIF media-profil-token (fra GetProfiles). Strøm-URI slås opp i drift. */
    mediaProfilToken: trygtRef,
    strom: z.enum(['primær', 'sekundær', 'snapshot']).default('primær'),
    /** Kodek slik enheten rapporterer den (H264/H265/MJPEG). Aldri antatt. */
    kodek: z.string().max(32).optional(),
});
// ── Kameraets optikk ────────────────────────────────────────────────────────
/**
 * Sensortype. Hvert OPTISK HODE er sitt eget `PtzKamera` (egen ONVIF media-profil,
 * eget synsfelt), selv når dag- og termisk-hodet sitter på samme dreieplattform;
 * de knyttes sammen med `plattformId`. Alternativet — ett kamera med en liste
 * optikker — ville gjort at hver geometri-funksjon måtte spørre «hvilket hode?».
 */
export const KameraSensorType = z.enum([
    'dag', // vanlig farge-CMOS
    'lavlys', // forsterket dag/natt (fortsatt synlig lys)
    'ir', // dag/natt-kamera med IR-belysning (nær-IR, aktiv — lyser opp = avslører seg)
    'termisk', // LWIR, passiv. Ser varme, ikke detaljer — ID er ofte umulig, deteksjon god.
]);
export const ZoomKalibreringspunkt = z.object({
    /** ONVIF generisk zoom (0 = vidvinkel, 1 = tele). */
    zoom: z.number().min(0).max(1),
    brennviddeMm: z.number().positive(),
});
export const KameraOptikk = z
    .object({
    /** Sensorens aktive bredde/høyde i mm. Kreves for synsfelt (2·atan(d/2f)). */
    sensorBreddeMm: z.number().positive().max(100),
    sensorHoydeMm: z.number().positive().max(100),
    /** Vidvinkel-enden (kortest brennvidde). */
    brennviddeMinMm: z.number().positive().max(5000),
    /** Tele-enden (lengst brennvidde). */
    brennviddeMaksMm: z.number().positive().max(5000),
    /**
     * MÅLT sammenheng zoom↔brennvidde. Tom liste = vi antar lineært mellom min og
     * maks, som ONVIF eksplisitt IKKE garanterer (se filhodet punkt 4). Med to
     * eller flere punkter interpoleres det stykkevis lineært i stedet.
     */
    zoomKalibrering: z.array(ZoomKalibreringspunkt).default([]),
})
    .refine((o) => o.brennviddeMaksMm >= o.brennviddeMinMm, 'brennviddeMaks < brennviddeMin');
/** Synsfelt (grader) for en brennvidde og en sensordimensjon: 2·atan(d/2f). */
export function synsfeltDeg(brennviddeMm, sensorMm) {
    if (!(brennviddeMm > 0) || !(sensorMm > 0))
        return 0;
    return 2 * atanDeg(sensorMm / (2 * brennviddeMm));
}
/** Invers: brennvidde som gir ønsket synsfelt. */
export function brennviddeForSynsfeltMm(synsfeltGrader, sensorMm) {
    const fov = klem(synsfeltGrader, 0.001, 179.9);
    return sensorMm / (2 * tanDeg(fov / 2));
}
/** ONVIF generisk zoom (0..1) → brennvidde. Bruker kalibrering når den finnes. */
export function zoomTilBrennviddeMm(zoom, optikk) {
    const z = klem(zoom, 0, 1);
    const kal = [...optikk.zoomKalibrering].sort((a, b) => a.zoom - b.zoom);
    if (kal.length >= 2) {
        if (z <= kal[0].zoom)
            return kal[0].brennviddeMm;
        for (let i = 1; i < kal.length; i++) {
            if (z <= kal[i].zoom) {
                const a = kal[i - 1];
                const b = kal[i];
                const t = b.zoom === a.zoom ? 0 : (z - a.zoom) / (b.zoom - a.zoom);
                return a.brennviddeMm + t * (b.brennviddeMm - a.brennviddeMm);
            }
        }
        return kal[kal.length - 1].brennviddeMm;
    }
    // UVERIFISERT antagelse: lineær i brennvidde. Ekte objektiver er ofte
    // logaritmiske. Mål kameraet og fyll `zoomKalibrering` før operativ bruk.
    return optikk.brennviddeMinMm + z * (optikk.brennviddeMaksMm - optikk.brennviddeMinMm);
}
/** Invers av `zoomTilBrennviddeMm` (samme kalibrering, monotont antatt). */
export function brennviddeTilZoom(brennviddeMm, optikk) {
    const f = klem(brennviddeMm, optikk.brennviddeMinMm, optikk.brennviddeMaksMm);
    const kal = [...optikk.zoomKalibrering].sort((a, b) => a.brennviddeMm - b.brennviddeMm);
    if (kal.length >= 2) {
        if (f <= kal[0].brennviddeMm)
            return kal[0].zoom;
        for (let i = 1; i < kal.length; i++) {
            if (f <= kal[i].brennviddeMm) {
                const a = kal[i - 1];
                const b = kal[i];
                const t = b.brennviddeMm === a.brennviddeMm ? 0 : (f - a.brennviddeMm) / (b.brennviddeMm - a.brennviddeMm);
                return klem(a.zoom + t * (b.zoom - a.zoom), 0, 1);
            }
        }
        return kal[kal.length - 1].zoom;
    }
    const span = optikk.brennviddeMaksMm - optikk.brennviddeMinMm;
    if (!(span > 0))
        return 0;
    return klem((f - optikk.brennviddeMinMm) / span, 0, 1);
}
/** Synsfelt (H/V) ved en gitt normalisert zoom. */
export function synsfeltForZoom(optikk, zoom) {
    const f = zoomTilBrennviddeMm(zoom, optikk);
    return { hDeg: synsfeltDeg(f, optikk.sensorBreddeMm), vDeg: synsfeltDeg(f, optikk.sensorHoydeMm) };
}
/** Velg zoom som gir (minst) ønsket horisontalt synsfelt, klemt til optikken. */
export function zoomForSynsfeltH(optikk, onsketHDeg) {
    const onskeF = brennviddeForSynsfeltMm(onsketHDeg, optikk.sensorBreddeMm);
    let klemt = null;
    if (onskeF > optikk.brennviddeMaksMm)
        klemt = 'tele'; // trenger smalere FOV enn tele gir
    if (onskeF < optikk.brennviddeMinMm)
        klemt = 'vid'; // trenger videre FOV enn vidvinkel gir
    const f = klem(onskeF, optikk.brennviddeMinMm, optikk.brennviddeMaksMm);
    return {
        zoom: brennviddeTilZoom(f, optikk),
        brennviddeMm: f,
        synsfelt: {
            hDeg: synsfeltDeg(f, optikk.sensorBreddeMm),
            vDeg: synsfeltDeg(f, optikk.sensorHoydeMm),
        },
        klemt,
    };
}
// ── Kameraets oppsett ───────────────────────────────────────────────────────
export const PtzKapabilitet = z.object({
    /** AbsoluteMove for pan/tilt. Profile S §8.4 — BETINGET, aldri antatt. */
    absoluttPanTilt: z.boolean().default(false),
    absoluttZoom: z.boolean().default(false),
    relativPanTilt: z.boolean().default(false),
    kontinuerlig: z.boolean().default(false),
    /** MaximumNumberOfPresets. 0 = ingen preset-støtte. */
    presets: z.number().int().min(0).default(0),
    /** Tilbyr enheten SphericalPositionSpaceDegrees? Da slipper vi normalisert-gjetting. */
    gradromStottet: z.boolean().default(false),
    /** Tilbyr enheten PositionSpaceMillimeter (brennvidde direkte)? */
    brennviddeRomStottet: z.boolean().default(false),
    /** GetStatus rapporterer posisjon (ONVIF-kapabilitet StatusPosition). */
    statusPosisjon: z.boolean().default(false),
});
export const PtzKamera = z
    .object({
    kameraId: z.string().min(1),
    navn: z.string().min(1),
    /** Hoder som deler dreieplattform (dag + termisk) har samme plattformId. */
    plattformId: z.string().optional(),
    sensorType: KameraSensorType,
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    /**
     * Optisk senter over MIDDELVANN, ikke over bakken. Uten denne kan tilt ikke
     * regnes i det hele tatt. NB: vannstanden varierer (se `tide.ts`) — ±1 m
     * tidevann er ±0.07° tiltfeil på 800 m, altså neglisjerbart, men på 100 m er
     * det 0.57° og da begynner det å bety noe ved full tele.
     */
    hoydeOverHavetM: z.number().min(0).max(3000),
    /**
     * SANN peiling (0–360) som kameraets pan = 0 peker mot. UTEN DENNE KAN INGEN
     * PEILING OVERSETTES — ONVIF sitt pan=0 er en mekanisk nullstilling, ikke nord.
     * Målt med kompass? Korriger for misvisning FØRST (`magdec.ts`); her skal det
     * stå sann retning.
     */
    panNullRetningDeg: z.number().min(0).max(360),
    /** Mekaniske/oppsatte pan-grenser i kameraets eget gradrom. */
    panMinDeg: z.number().min(-360).max(360).default(-180),
    panMaksDeg: z.number().min(-360).max(360).default(180),
    /** Endeløs pan (ingen stopp). Da ignoreres panMin/panMaks. */
    panKontinuerlig: z.boolean().default(false),
    /** ONVIF-konvensjon: 0 = horisonten, negativ = nedover. */
    tiltMinDeg: z.number().min(-180).max(180).default(-90),
    tiltMaksDeg: z.number().min(-180).max(180).default(20),
    optikk: KameraOptikk,
    /**
     * Praktisk identifikasjonsavstand for DENNE optikken/sensoren. Ikke deteksjon
     * — identifikasjon. Settes i drift etter måling, aldri fra datablad.
     */
    maksIdentAvstandM: z.number().positive().optional(),
    /** Udefinert = kapabilitet ikke lest fra enheten ennå (vi antar ingenting). */
    kapabilitet: PtzKapabilitet.optional(),
    video: VideoReferanse.optional(),
})
    .refine((k) => k.tiltMaksDeg >= k.tiltMinDeg, 'tiltMaks < tiltMin')
    .refine((k) => k.panKontinuerlig || k.panMaksDeg >= k.panMinDeg, 'panMaks < panMin');
// ── Kameraets tilstand (grader, ikke normalisert) ───────────────────────────
/** ONVIF MoveStatus. 'ukjent' brukes bare ved oppstart/feil (spec §5.3). */
export const PtzBevegelse = z.enum(['stille', 'beveger', 'ukjent']);
export const PtzSporingModus = z.enum([
    'fri', // står stille, ingen oppdrag
    'manuell', // operatøren styrer
    'følger-mål', // autotrack på et spor
    'kø-peker', // pekt av en slew-to-cue og venter på ID
    'patrulje', // preset-tour
]);
export const PtzTilstand = z.object({
    kameraId: z.string().min(1),
    tsMs: z.number().int().nonnegative(),
    /**
     * Pan/tilt i GRADER i kameraets eget gradrom — UI-et og operatøren tenker i
     * grader, aldri i −1..1. Kommer normalisert fra enheten oversettes det med
     * `normalisertTilGrader` FØR det havner her.
     */
    panDeg: z.number().min(-360).max(360),
    tiltDeg: z.number().min(-180).max(180),
    /** Synsfeltet NÅ. Foretrekk enhetens egen FieldOfView (GetStatus) når den finnes. */
    synsfeltHDeg: z.number().positive().max(180),
    synsfeltVDeg: z.number().positive().max(180),
    brennviddeMm: z.number().positive().optional(),
    /** Rå ONVIF-zoom, om broen sender den (0 = vid, 1 = tele). */
    zoomNormalisert: z.number().min(0).max(1).optional(),
    bevegelse: PtzBevegelse.default('ukjent'),
    sporing: z
        .object({
        modus: PtzSporingModus.default('fri'),
        /** Sporet kameraet følger/venter på (`Track.id`). */
        malId: z.string().optional(),
        /** Hvem ba om pekingen (radarId, rf-nodeId, brukerId). */
        kilde: z.string().optional(),
    })
        .default({ modus: 'fri' }),
    /** ONVIF GetStatus.Error — vises til operatøren, aldri svelget. */
    feil: z.string().optional(),
});
export function panGrenser(kamera) {
    // Endeløs pan = hele sirkelen. ONVIF sitt gradrom er −180..180 for pan i
    // praksis (XRange er formelt uavgrenset, men −180 og +180 er samme retning).
    if (kamera.panKontinuerlig)
        return { minDeg: -180, maksDeg: 180 };
    return { minDeg: kamera.panMinDeg, maksDeg: kamera.panMaksDeg };
}
export function tiltGrenser(kamera) {
    return { minDeg: kamera.tiltMinDeg, maksDeg: kamera.tiltMaksDeg };
}
/**
 * Legg en vinkel i vinduet rundt aksens midtpunkt. HVORFOR: 200° og −160° er
 * samme retning. Klemmer vi 200° rått mot [−120,120] får vi +120 (80° feil vei);
 * wrapper vi først om midtpunktet får vi −160 → −120, som er 40° unna og faktisk
 * nærmeste tillatte retning. Dette er hele 0/360-fella i én linje.
 */
export function vinkelIVindu(deg, grenser) {
    const midt = (grenser.minDeg + grenser.maksDeg) / 2;
    return midt + vinkelWrap180(deg - midt);
}
/** Wrap til vindu, deretter klem til grensene. Full sirkel klemmes aldri. */
export function klemAkse(deg, grenser) {
    const span = grenser.maksDeg - grenser.minDeg;
    const v = vinkelIVindu(deg, grenser);
    if (span >= 359.999)
        return { deg: v, klemt: false };
    if (v < grenser.minDeg)
        return { deg: grenser.minDeg, klemt: true };
    if (v > grenser.maksDeg)
        return { deg: grenser.maksDeg, klemt: true };
    return { deg: v, klemt: false };
}
/**
 * ONVIF generisk normalisert akse (−1..1) → grader. −1 er aksens ene ende, +1 den
 * andre, og MELLOM dem antar vi lineært. Det er en antagelse spec ikke gir oss
 * (den sier bare «full range … normalized to −1 to 1»), men den er den eneste
 * tilgjengelige uten kalibrering — derfor: bruk gradrommet
 * (SphericalPositionSpaceDegrees) når enheten tilbyr det.
 */
export function normalisertTilGrader(normalisert, grenser) {
    const span = grenser.maksDeg - grenser.minDeg;
    if (!(span > 0))
        return grenser.minDeg; // degenerert akse (låst)
    const n = klem(Number.isFinite(normalisert) ? normalisert : 0, -1, 1);
    return grenser.minDeg + ((n + 1) / 2) * span;
}
/** Grader → ONVIF generisk normalisert (−1..1). Wrapper og klemmer først. */
export function graderTilNormalisert(deg, grenser) {
    const span = grenser.maksDeg - grenser.minDeg;
    if (!(span > 0))
        return 0;
    const v = klemAkse(deg, grenser).deg;
    return klem(((v - grenser.minDeg) / span) * 2 - 1, -1, 1);
}
/** Kameraets pan-grader → SANN peiling (0–360). */
export function panTilPeiling(kamera, panDeg) {
    return peiling360(panDeg + kamera.panNullRetningDeg);
}
/** SANN peiling → kameraets pan-grader (wrappet i aksevinduet, ikke klemt). */
export function peilingTilPan(kamera, peiling) {
    return vinkelIVindu(peiling - kamera.panNullRetningDeg, panGrenser(kamera));
}
/**
 * 0.2 = målet fyller 1/5 av bildebredden. Hvorfor ikke tettere: pekingen har
 * feil (peilingsfeil fra kilden, nordreferanse-feil, backlash), og målet flytter
 * seg mellom kø og ferdig sving. Fyller målet hele bildet, er 1° feil nok til at
 * bildet er tomt. 1/5 gir slakk OG nok piksler til å se skrog og folk.
 */
export const STANDARD_FYLLINGSGRAD = 0.2;
/** Nærgrense for dekningsvifta ved peiling uten avstand (m). */
export const STANDARD_DEKNING_NAER_M = 300;
/** Slakk på dekningsvifta (10 %) — mot tiltfeil og bølgehøyde. */
export const STANDARD_DEKNING_MARGIN = 0.1;
/** Under denne avstanden er peilingen til målet meningsløs. */
const NULLAVSTAND_M = 0.5;
/**
 * SLEW-TO-CUE, hovedfunksjonen: pan/tilt/zoom for å SE et geografisk punkt.
 *
 * Tilten regnes fra HØYDEFORSKJELL og AVSTAND, ikke satt til 0. Et kamera 30 m
 * over havet som ser 800 m ut peker 2.15° NEDOVER. Ved full tele er det vertikale
 * synsfeltet gjerne 0.7–1°, altså peker et kamera som ignorerer høyden mer enn to
 * hele bildehøyder over målet — det ser tom horisont og operatøren konkluderer
 * «ingenting der». Det er den vanligste og dyreste feilen i slew-to-cue.
 */
export function pekMotPunkt(kamera, mal) {
    const begrensninger = [];
    const fra = { lat: kamera.lat, lon: kamera.lon };
    const til = { lat: mal.lat, lon: mal.lon };
    const dHoriz = haversineM(fra, til);
    const malHoyde = mal.hoydeM ?? 0;
    const dHoyde = kamera.hoydeOverHavetM - malHoyde;
    // Peiling. Rett under kameraet finnes ingen peiling — da beholder vi pan der
    // det er (uttrykt som nåværende nullretning) og sier det høyt.
    let peiling;
    if (dHoriz < NULLAVSTAND_M) {
        peiling = kamera.panNullRetningDeg;
        begrensninger.push('mål-i-kamerapunkt');
    }
    else {
        peiling = bearingDeg(fra, til);
    }
    // Tilt: depresjonsvinkel. atan2 håndterer dHoriz = 0 (rett ned = −90°) uten
    // divisjon på null, og negativ dHoyde (mål OVER kameraet) gir positiv tilt.
    const fall = (mal.jordkrumning ?? false) ? jordkrumningsfallM(dHoriz) : 0;
    const onsketTilt = -tilDeg(Math.atan2(dHoyde + fall, dHoriz));
    const pg = panGrenser(kamera);
    const tg = tiltGrenser(kamera);
    const onsketPan = vinkelIVindu(peiling - kamera.panNullRetningDeg, pg);
    const pan = klemAkse(onsketPan, pg);
    const tilt = klemAkse(onsketTilt, tg);
    if (pan.klemt)
        begrensninger.push('utenfor-pan-grense');
    if (tilt.klemt)
        begrensninger.push('utenfor-tilt-grense');
    const skra = Math.hypot(dHoriz, dHoyde);
    // Zoom: la målet fylle en fornuftig del av bildet. Uten antatt målstørrelse
    // GJETTER VI IKKE — vi står i vidvinkel og sier at zoom ikke er valgt.
    let valg;
    if (mal.storrelseM !== undefined && mal.storrelseM > 0 && skra > NULLAVSTAND_M) {
        const fyll = klem(mal.fyllingsgrad ?? STANDARD_FYLLINGSGRAD, 0.01, 1);
        const malVinkelDeg = 2 * atanDeg(mal.storrelseM / 2 / skra);
        valg = zoomForSynsfeltH(kamera.optikk, malVinkelDeg / fyll);
        if (valg.klemt === 'tele')
            begrensninger.push('zoom-klemt-tele');
        if (valg.klemt === 'vid')
            begrensninger.push('zoom-klemt-vid');
    }
    else {
        begrensninger.push('ukjent-målstørrelse');
        const f = kamera.optikk.brennviddeMinMm;
        const z = brennviddeTilZoom(f, kamera.optikk);
        valg = { zoom: z, brennviddeMm: f, synsfelt: synsfeltForZoom(kamera.optikk, z), klemt: null };
    }
    // Fysikk før optikk: et sjømål bak horisonten kan ikke ses uansett zoom.
    const horisont = horisontAvstandM(kamera.hoydeOverHavetM, malHoyde);
    const bakHorisonten = dHoriz > horisont;
    if (bakHorisonten)
        begrensninger.push('bak-horisonten');
    if (kamera.maksIdentAvstandM !== undefined && skra > kamera.maksIdentAvstandM) {
        // Kan pekes, men ID blir ikke mulig. Bryter IKKE `naadd`.
        begrensninger.push('over-ident-avstand');
    }
    let kanAbsolutt = true;
    if (!kamera.kapabilitet) {
        begrensninger.push('kapabilitet-ukjent');
    }
    else if (!kamera.kapabilitet.absoluttPanTilt) {
        begrensninger.push('ingen-absolutt-peking');
        kanAbsolutt = false;
    }
    const naadd = !pan.klemt &&
        !tilt.klemt &&
        !bakHorisonten &&
        kanAbsolutt &&
        !begrensninger.includes('mål-i-kamerapunkt');
    return {
        kameraId: kamera.kameraId,
        peilingDeg: peiling360(peiling),
        panDeg: pan.deg,
        tiltDeg: tilt.deg,
        onsketPanDeg: onsketPan,
        onsketTiltDeg: onsketTilt,
        panNormalisert: graderTilNormalisert(pan.deg, pg),
        tiltNormalisert: graderTilNormalisert(tilt.deg, tg),
        zoomNormalisert: valg.zoom,
        brennviddeMm: valg.brennviddeMm,
        synsfelt: valg.synsfelt,
        horisontalAvstandM: dHoriz,
        skraAvstandM: skra,
        naadd,
        begrensninger,
    };
}
/** Sporet vi vil se på — samme fusjonerte `Track` COP-en viser. */
export function pekMotTrack(kamera, spor, mal) {
    return pekMotPunkt(kamera, { ...mal, lat: spor.lat, lon: spor.lon });
}
/**
 * Peking på en peiling UTEN avstand (Kraken-RF, hydrofon, akustikk).
 *
 * UTEN AVSTAND KAN TILT IKKE REGNES — depresjonsvinkelen er en funksjon av
 * avstanden. Vi nekter å gjette en avstand og late som den er kjent. I stedet
 * dekker vi et avstandsSPENN: velg tilt midt mellom depresjonsvinkelen til
 * `naerM` og til `fjernM`, og velg zoom slik at det VERTIKALE synsfeltet spenner
 * hele forskjellen. Da er alt mellom nær og fjern i bildet samtidig, og
 * operatøren får ett bilde å søke i istedenfor en peiling og et gjett.
 *
 * Eksempel (kamera 30 m, spenn 300–2000 m): depresjon 5.71° og 0.86°, altså
 * tilt ≈ −3.3° og et krav om ~4.9° vertikalt synsfelt. Kan optikken ikke gi
 * det, sier vi `fullDekning: false` og rapporterer hva som FAKTISK dekkes —
 * aldri et tall som later som mer.
 *
 * Får du likevel en avstand (`antattAvstandM`), regner vi punkt-løsningen og
 * merker den 'avstand-antatt', slik at usikkerheten følger med videre.
 */
export function pekMotPeiling(kamera, peilingDeg, antattAvstandM, opts = {}) {
    const malHoyde = opts.malHoydeM ?? 0;
    const horisont = horisontAvstandM(kamera.hoydeOverHavetM, malHoyde);
    // Har vi (antatt) avstand: projiser og bruk punkt-løsningen. Merket, ikke skjult.
    if (antattAvstandM !== undefined && antattAvstandM > NULLAVSTAND_M) {
        const punkt = project({ lat: kamera.lat, lon: kamera.lon }, peiling360(peilingDeg), antattAvstandM);
        const los = pekMotPunkt(kamera, { lat: punkt.lat, lon: punkt.lon, hoydeM: malHoyde });
        los.begrensninger.push('avstand-antatt');
        return los;
    }
    const begrensninger = ['avstand-ukjent'];
    const naer = Math.max(NULLAVSTAND_M, opts.naerM ?? STANDARD_DEKNING_NAER_M);
    const fjernOnsket = opts.fjernM ?? Math.min(kamera.maksIdentAvstandM ?? Infinity, horisont);
    const fjern = Math.max(naer * 1.01, Math.min(fjernOnsket, horisont));
    const dHoyde = kamera.hoydeOverHavetM - malHoyde;
    const pg = panGrenser(kamera);
    const tg = tiltGrenser(kamera);
    const onsketPan = vinkelIVindu(peilingDeg - kamera.panNullRetningDeg, pg);
    const pan = klemAkse(onsketPan, pg);
    if (pan.klemt)
        begrensninger.push('utenfor-pan-grense');
    // Uten høydeforskjell finnes ingen depresjonsvinkel — da er horisonten det
    // eneste ærlige valget, og dekningen er ukjent.
    if (dHoyde <= NULLAVSTAND_M) {
        begrensninger.push('kan-ikke-regne-tilt', 'dekning-ufullstendig');
        const tilt = klemAkse(0, tg);
        const f = kamera.optikk.brennviddeMinMm;
        const zoom = brennviddeTilZoom(f, kamera.optikk);
        return {
            kameraId: kamera.kameraId,
            peilingDeg: peiling360(peilingDeg),
            panDeg: pan.deg,
            tiltDeg: tilt.deg,
            onsketPanDeg: onsketPan,
            onsketTiltDeg: 0,
            panNormalisert: graderTilNormalisert(pan.deg, pg),
            tiltNormalisert: graderTilNormalisert(tilt.deg, tg),
            zoomNormalisert: zoom,
            brennviddeMm: f,
            synsfelt: synsfeltForZoom(kamera.optikk, zoom),
            horisontalAvstandM: NaN, // ærlig: vi VET ikke avstanden
            skraAvstandM: NaN,
            naadd: !pan.klemt,
            begrensninger,
            dekning: { naerM: naer, fjernM: fjern, fullDekning: false },
        };
    }
    const tiltNaer = -atanDeg(dHoyde / naer); // mest nedover
    const tiltFjern = -atanDeg(dHoyde / fjern); // minst nedover
    const kravVDeg = (tiltFjern - tiltNaer) * (1 + (opts.margin ?? STANDARD_DEKNING_MARGIN));
    const onsketTilt = (tiltNaer + tiltFjern) / 2;
    const tilt = klemAkse(onsketTilt, tg);
    if (tilt.klemt)
        begrensninger.push('utenfor-tilt-grense');
    // Brennvidde fra det VERTIKALE kravet (dekningsvifta), deretter utvidet hvis
    // peilingsusikkerheten krever bredere bilde horisontalt.
    let f = brennviddeForSynsfeltMm(kravVDeg, kamera.optikk.sensorHoydeMm);
    const usikkerhet = opts.usikkerhetDeg ?? 0;
    if (usikkerhet > 0) {
        const fH = brennviddeForSynsfeltMm(2 * usikkerhet, kamera.optikk.sensorBreddeMm);
        f = Math.min(f, fH); // kortere brennvidde = videre bilde
    }
    if (f > kamera.optikk.brennviddeMaksMm)
        begrensninger.push('zoom-klemt-tele');
    if (f < kamera.optikk.brennviddeMinMm)
        begrensninger.push('zoom-klemt-vid');
    f = klem(f, kamera.optikk.brennviddeMinMm, kamera.optikk.brennviddeMaksMm);
    const zoom = brennviddeTilZoom(f, kamera.optikk);
    const synsfelt = {
        hDeg: synsfeltDeg(f, kamera.optikk.sensorBreddeMm),
        vDeg: synsfeltDeg(f, kamera.optikk.sensorHoydeMm),
    };
    const dekning = sjodekning(dHoyde, tilt.deg, synsfelt.vDeg, horisont);
    const full = dekning.naerM <= naer * 1.02 && dekning.fjernM >= Math.min(fjern, horisont) * 0.98;
    if (!full)
        begrensninger.push('dekning-ufullstendig');
    return {
        kameraId: kamera.kameraId,
        peilingDeg: peiling360(peilingDeg),
        panDeg: pan.deg,
        tiltDeg: tilt.deg,
        onsketPanDeg: onsketPan,
        onsketTiltDeg: onsketTilt,
        panNormalisert: graderTilNormalisert(pan.deg, pg),
        tiltNormalisert: graderTilNormalisert(tilt.deg, tg),
        zoomNormalisert: zoom,
        brennviddeMm: f,
        synsfelt,
        horisontalAvstandM: NaN,
        skraAvstandM: NaN,
        naadd: !pan.klemt && !tilt.klemt,
        begrensninger,
        dekning: { ...dekning, fullDekning: full },
    };
}
/**
 * Hvilket avstandsspenn på SJØOVERFLATEN dekkes av et bilde med gitt tilt og
 * vertikalt synsfelt. Øvre bildekant nær eller over horisonten dekker i praksis
 * til horisonten — vi kapper der, framfor å returnere Infinity som ingen kan
 * bruke operativt.
 */
export function sjodekning(hoydeOverMalM, tiltDeg, synsfeltVDeg, horisontM) {
    const ovre = tiltDeg + synsfeltVDeg / 2; // minst depresjon → lengst ut
    const nedre = tiltDeg - synsfeltVDeg / 2; // mest depresjon → nærmest
    const rekkevidde = (t) => (t < -0.001 ? hoydeOverMalM / tanDeg(-t) : horisontM);
    return {
        naerM: Math.min(rekkevidde(nedre), horisontM),
        fjernM: Math.min(rekkevidde(ovre), horisontM),
    };
}
// ── Kø (cue) fra en annen sensor ────────────────────────────────────────────
export const PekKoKilde = z.enum(['radar', 'rf', 'hydrofon', 'akustisk', 'ais', 'kamera', 'manuell']);
export const PekKo = z.object({
    kilde: PekKoKilde,
    /** Hvilken node/radar/RF-mottaker som køet. Kildesporing, jf. `Track.source`. */
    kildeId: z.string().optional(),
    /** Sporet dette gjelder (`Track.id`), om det finnes. */
    malId: z.string().optional(),
    tsMs: z.number().int().nonnegative(),
    /** SANN peiling fra KILDENS posisjon — ikke fra kameraets. */
    peilingDeg: z.number().min(0).max(360).optional(),
    avstandM: z.number().positive().optional(),
    /** Kildens posisjon. Uten den må vi anta at kilden står der kameraet står. */
    kildeLat: z.number().min(-90).max(90).optional(),
    kildeLon: z.number().min(-180).max(180).optional(),
    /** Løst punkt (multilaterasjon, radarspor, AIS) — beste tilfelle. */
    lat: z.number().min(-90).max(90).optional(),
    lon: z.number().min(-180).max(180).optional(),
    /** Peilingsusikkerhet ± grader (RF-lobebredde, hydrofon-oppløsning). */
    usikkerhetDeg: z.number().min(0).max(180).optional(),
});
/**
 * Slew-to-cue-inngangen: gjør en kø fra radar/RF/hydrofon om til pan/tilt/zoom.
 * Rekkefølgen er bevisst — best tilgjengelige informasjon vinner:
 *   1. løst punkt (lat/lon) → punkt-peking,
 *   2. peiling + avstand fra en kilde med kjent posisjon → projiser, punkt-peking.
 *      PARALLAKSE ER EKTE: står radaren 40 m fra kameraet, er radarens peiling
 *      ikke kameraets. Mangler `kildeLat/kildeLon` antar vi kameraets posisjon og
 *      MERKER det ('kø-opprinnelse-antatt-kamera') — vi later ikke som det er gratis.
 *   3. bare peiling → dekningsvifte (`pekMotPeiling`).
 */
export function pekMotKo(kamera, ko, mal) {
    if (ko.lat !== undefined && ko.lon !== undefined) {
        return pekMotPunkt(kamera, { ...mal, lat: ko.lat, lon: ko.lon });
    }
    if (ko.peilingDeg !== undefined && ko.avstandM !== undefined) {
        const antattKamerakilde = ko.kildeLat === undefined || ko.kildeLon === undefined;
        const opprinnelse = antattKamerakilde
            ? { lat: kamera.lat, lon: kamera.lon }
            : { lat: ko.kildeLat, lon: ko.kildeLon };
        const punkt = project(opprinnelse, ko.peilingDeg, ko.avstandM);
        const los = pekMotPunkt(kamera, { ...mal, lat: punkt.lat, lon: punkt.lon });
        if (antattKamerakilde)
            los.begrensninger.push('kø-opprinnelse-antatt-kamera');
        return los;
    }
    if (ko.peilingDeg !== undefined) {
        return pekMotPeiling(kamera, ko.peilingDeg, undefined, {
            usikkerhetDeg: ko.usikkerhetDeg,
            malHoydeM: mal?.hoydeM,
        });
    }
    // Kø uten både punkt og peiling er ikke en kø. Vi peker ikke i blinde.
    const los = pekMotPeiling(kamera, panTilPeiling(kamera, 0), undefined, {});
    los.begrensninger.push('kan-ikke-regne-tilt');
    los.naadd = false;
    return los;
}
/**
 * Ser kameraet dette punktet NÅ? Brukes til «hvilke sensorer observerer denne
 * kontakten» — svaret må derfor kunne bli NEI av gode grunner, og si hvilken.
 *
 * ADVARSEL (dokumentert begrensning, ikke en bug): dette er REN GEOMETRI. Det
 * finnes ingen sikt-linje-sjekk mot terreng her — en holme eller et nes mellom
 * kamera og punkt gir fortsatt `dekker: true`. Terrengmaskering hører til
 * `terrain.ts`/`elevservice.ts` og må legges oppå. Vi later ikke som denne
 * funksjonen kan mer enn den kan.
 *
 * NAVN (SENS-26): het tidligere `dekkerPunkt`, samme navn som funksjonen i
 * `ugs.ts`. De er IKKE duplisert logikk — dette er kameraets NÅVÆRENDE
 * pekeretning (avhenger av tilstand: pan/tilt NÅ, alder på tilstanden, om
 * kameraet er i sving) mot en STATISK sensors faste dekningsgeometri
 * (rundstrålende/sektor/linje, ingen tidsdimensjon). Ulikt returtype
 * (rikt `DekningsSvar` her, `boolean` i `ugs.ts`) og ulik inngang bekrefter
 * det. De skal derfor IKKE slås sammen — de er gitt hvert sitt entydige navn
 * så en fremtidig barrel-eksport av `ptz.ts` (se SENS-29) ikke skygger `ugs.ts`
 * sin variant i stillhet.
 */
export function dekkerPunktPtz(kamera, punkt, opts = {}) {
    const { oppsett, tilstand } = kamera;
    const arsaker = [];
    const fra = { lat: oppsett.lat, lon: oppsett.lon };
    const dHoriz = haversineM(fra, punkt);
    const malHoyde = opts.malHoydeM ?? 0;
    const dHoyde = oppsett.hoydeOverHavetM - malHoyde;
    const peiling = dHoriz < NULLAVSTAND_M ? panTilPeiling(oppsett, tilstand.panDeg) : bearingDeg(fra, punkt);
    const kravPan = vinkelIVindu(peiling - oppsett.panNullRetningDeg, panGrenser(oppsett));
    const kravTilt = -tilDeg(Math.atan2(dHoyde, dHoriz));
    const avvikPan = vinkelWrap180(kravPan - tilstand.panDeg);
    const avvikTilt = kravTilt - tilstand.tiltDeg;
    const halvH = tilstand.synsfeltHDeg / 2;
    const halvV = tilstand.synsfeltVDeg / 2;
    const bildeX = halvH > 0 ? avvikPan / halvH : Infinity;
    const bildeY = halvV > 0 ? avvikTilt / halvV : Infinity;
    if (Math.abs(bildeX) > 1)
        arsaker.push('utenfor-pan');
    if (Math.abs(bildeY) > 1)
        arsaker.push('utenfor-tilt');
    if (dHoriz > horisontAvstandM(oppsett.hoydeOverHavetM, malHoyde))
        arsaker.push('bak-horisonten');
    const skra = Math.hypot(dHoriz, dHoyde);
    if (oppsett.maksIdentAvstandM !== undefined && skra > oppsett.maksIdentAvstandM) {
        arsaker.push('over-ident-avstand');
    }
    const na = opts.naMs ?? Date.now();
    if (na - tilstand.tsMs > (opts.maksAlderMs ?? 5000))
        arsaker.push('utdatert-tilstand');
    if (tilstand.bevegelse === 'beveger')
        arsaker.push('kamera-i-bevegelse');
    return {
        // 'over-ident-avstand' hindrer ikke DEKNING (punktet er i bildet), bare ID.
        dekker: !arsaker.some((a) => a !== 'over-ident-avstand'),
        peilingDeg: peiling360(peiling),
        horisontalAvstandM: dHoriz,
        avvikPanDeg: avvikPan,
        avvikTiltDeg: avvikTilt,
        bildeX,
        bildeY,
        arsaker,
    };
}
// ── Konvolutt over WS (samme mønster som radar.ts) ─────────────────────────
export const PTZ_KANAL = 'ptz';
export const PtzMelding = z.discriminatedUnion('type', [
    z.object({ type: z.literal('oppsett'), kamera: PtzKamera }),
    z.object({ type: z.literal('tilstand'), tilstand: PtzTilstand }),
    z.object({
        type: z.literal('borte'),
        kameraId: z.string().min(1),
        /** Hvorfor broen mistet kameraet — vises, aldri skjult. */
        arsak: z.string(),
    }),
]);
/**
 * Kommandoer mot kameraet som EFFEKTOR. Absolutt peking sendes i GRADER; broen
 * oversetter til enhetens rom (gradrom hvis støttet, ellers normalisert via
 * `graderTilNormalisert`). Grunnen til at kontrakten er i grader: en logget
 * kommando skal kunne leses av et menneske ett år senere.
 */
export const PtzKommando = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('pekAbsolutt'),
        kameraId: z.string().min(1),
        panDeg: z.number().min(-360).max(360),
        tiltDeg: z.number().min(-180).max(180),
        zoomNormalisert: z.number().min(0).max(1).optional(),
    }),
    z.object({ type: z.literal('pekMotKo'), kameraId: z.string().min(1), ko: PekKo }),
    z.object({ type: z.literal('folgMal'), kameraId: z.string().min(1), malId: z.string().min(1) }),
    z.object({ type: z.literal('slippMal'), kameraId: z.string().min(1) }),
    z.object({ type: z.literal('stopp'), kameraId: z.string().min(1) }),
    z.object({
        type: z.literal('gaaTilPreset'),
        kameraId: z.string().min(1),
        /** ONVIF PresetToken — opak, kommer fra enheten. */
        presetToken: trygtRef,
    }),
]);
/** Parse en PTZ-kommando. Null ved ugyldig (aldri kast) — som parseSparkControl. */
export function parsePtzKommando(raw) {
    const p = PtzKommando.safeParse(raw);
    return p.success ? p.data : null;
}
//# sourceMappingURL=ptz.js.map